import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Scorm12API } from 'scorm-again';
import { getUser } from '../lib/api';
import EngineCoursePlayer from './EngineCoursePlayer';

export default function CoursePlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const apiRef = useRef(null);
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState(null);
  const [apiReady, setApiReady] = useState(false);
  const [engineEnrollmentId, setEngineEnrollmentId] = useState(null);

  useEffect(() => {
    if (!getUser()) {
      navigate(`/login?next=/courses/${slug}/play`);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const enrolls = await fetch('/api/me/enrollments', {
          credentials: 'include',
        }).then((r) => r.json());

        const found = enrolls.enrollments?.find((e) => e.course_slug === slug);
        if (!found) {
          if (!cancelled) setError('Not enrolled in this course.');
          return;
        }
        if (found.content_format === 'engine') {
          if (!cancelled) setEngineEnrollmentId(found.id);
          return;
        }

        const play = await fetch(`/api/learning/play/${found.id}`, {
          credentials: 'include',
        }).then((r) => r.json());

        if (cancelled) return;
        setEnrollment(play);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  useEffect(() => {
    if (!enrollment) return;

    const api = new Scorm12API({
      autocommit: true,
      autocommitSeconds: 30,
      logLevel: 4,
    });

    if (enrollment.cmi_data && Object.keys(enrollment.cmi_data).length) {
      try { api.loadFromJSON(enrollment.cmi_data); } catch (err) {
        console.warn('Failed to load prior CMI:', err);
      }
    }

    const persist = async () => {
      try {
        const cmi_data = api.cmi.toJSON ? api.cmi.toJSON() : JSON.parse(JSON.stringify(api.cmi));
        await fetch(`/api/learning/scorm/${enrollment.enrollment_id}/save`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cmi_data }),
        });
      } catch (err) {
        console.error('CMI save failed:', err);
      }
    };
    api.on('LMSCommit', persist);
    api.on('LMSFinish', persist);

    window.API = api;
    apiRef.current = api;
    setApiReady(true);

    return () => {
      persist();
      try { delete window.API; } catch { window.API = undefined; }
    };
  }, [enrollment]);

  if (engineEnrollmentId) return <EngineCoursePlayer enrollmentId={engineEnrollmentId} />;

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-xl font-semibold text-red-600">Can't open course</h1>
        <p className="mt-2 text-slate-600">{error}</p>
        <button onClick={() => navigate('/dashboard')} className="mt-4 text-brand underline">
          Back to dashboard
        </button>
      </div>
    );
  }

  if (!enrollment || !apiReady) {
    return <div className="max-w-2xl mx-auto px-4 py-12 text-slate-500">Loading course…</div>;
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white text-sm">
        <span className="font-medium">{enrollment.course_title}</span>
        <button onClick={() => navigate('/dashboard')} className="text-slate-300 hover:text-white">
          Exit course
        </button>
      </div>
      <iframe
        src={enrollment.launch_url}
        title={enrollment.course_title}
        className="flex-1 w-full border-0 bg-white"
        allow="autoplay; fullscreen"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    </div>
  );
}
